import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { actionCreators } from "@App/state";
import { joinGermanNumberPlate } from "@App/utils/numberPlate";
import { bindActionCreators } from "@reduxjs/toolkit";

/**
 * Checks if any customer-related data is found in the query parameters and saves them to the store.
 */
export const useCustomerParameters = () => {
  const dispatch = useDispatch();
  const { setFirstName, setLastName, setEmail, setPhone, setNumberPlate } = bindActionCreators(
    actionCreators,
    dispatch,
  );

  const [searchParams] = useSearchParams();

  const sanitize = (stringValue: string | undefined | null) => {
    if (typeof stringValue === "string" && stringValue.trim().length > 0) {
      return decodeURIComponent(stringValue.trim());
    }
    return null;
  };

  useEffect(() => {
    // get url params
    const firstname = sanitize(searchParams.get("firstname") || searchParams.get("firstName"));
    const lastname = sanitize(searchParams.get("lastname") || searchParams.get("lastName"));
    const mail = sanitize(searchParams.get("mail"));
    const phoneCountry = sanitize(searchParams.get("phoneCountry"));
    const phoneNumber = sanitize(searchParams.get("phoneNumber"));
    const numberPlateRegion = sanitize(
      searchParams.get("numberplateRegion") || searchParams.get("numberPlateRegion"),
    );
    const numberPlateFirstID = sanitize(
      searchParams.get("numberplateFirstID") ||
        searchParams.get("numberPlateFirstId") ||
        searchParams.get("numberPlateFirstId"),
    );
    const numberPlateSecondId = sanitize(
      searchParams.get("numberplateSecondId") || searchParams.get("numberPlateSecondId"),
    );

    firstname && setFirstName(firstname);
    lastname && setLastName(lastname);
    mail && setEmail(mail);
    phoneCountry && phoneNumber && setPhone({ country: phoneCountry, number: phoneNumber });
    // The three separate params predate plates being a single string and stay supported
    // for existing partner deep links.
    numberPlateRegion &&
      numberPlateFirstID &&
      numberPlateSecondId &&
      setNumberPlate(
        joinGermanNumberPlate({
          region: numberPlateRegion,
          firstID: numberPlateFirstID,
          secondID: numberPlateSecondId,
        }),
      );
  }, [searchParams]);

  const customerQueryParams = (): Record<string, string> => {
    return {
      // returning nothing for now, because we just want the customer values to be set once
      // and never overwritten.
    };
  };

  return { customerQueryParams };
};
